import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import type { InputHTMLAttributes } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import dayjs from 'dayjs';
import { makeStyles } from 'tss-react/mui';

import Typography from '@/components/common/Typography';
import { TimePicker } from '@/components/common/Inputs';
import { CourierSchedule, TimeType } from '@/common/types';
import { DayEnum } from '@/common/enums';
import { buildEnabledDays, normalizeWorkingHours } from '@/utils/schedule';

type EnabledDays = Record<DayEnum, boolean>;

interface Props {
  value: CourierSchedule[];
  onChange: (_nextValue: CourierSchedule[]) => void;
  disabled?: boolean;
  enabledDays?: EnabledDays;
  onEnabledDaysChange?: (_nextValue: EnabledDays) => void;
}

const useStyles = makeStyles()(() => ({
  dayRow: {
    alignItems: 'center',
    columnGap: 5,
    justifyContent: 'flex-start'
  },
  dayCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    '@media (max-width: 900px)': {
      paddingTop: 0
    },
    marginTop: '25px'
  },
  dayLabel: {
    minWidth: 130,
    marginLeft: 0,
    justifyContent: 'flex-start',
    width: '100%'
  },
  timeColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6
  },
  timePickerCompact: {
    width: 130,
    '& input': {
      padding: '10px 12px',
      fontSize: 14,
      paddingRight: 5
    },
    '& .MuiInputAdornment-root': {
      marginLeft: 0
    },
    '& .MuiIconButton-root': {
      padding: 2
    },
    '& .MuiSvgIcon-root': {
      fontSize: 18
    }
  }
}));

const WeeklySchedule = ({ value, onChange, disabled = false, enabledDays: enabledDaysProp, onEnabledDaysChange }: Props) => {
  const { classes } = useStyles();
  const normalized = useMemo(() => normalizeWorkingHours(value), [value]);
  const isControlled = typeof enabledDaysProp !== 'undefined' && typeof onEnabledDaysChange === 'function';
  const [enabledDaysState, setEnabledDaysState] = useState<EnabledDays>(() => buildEnabledDays(normalized));
  const enabledDays = isControlled ? enabledDaysProp : enabledDaysState;

  useEffect(() => {
    const next = { ...(enabledDays || {}) } as EnabledDays;
    let changed = false;
    Object.values(DayEnum).forEach((day) => {
      const entry = normalized.find((item) => item.day === day);
      const hasTime = Boolean(entry?.startTime || entry?.endTime);
      if (hasTime && !next[day]) {
        next[day] = true;
        changed = true;
      }
      if (typeof next[day] === 'undefined') {
        next[day] = false;
        changed = true;
      }
    });
    if (changed) {
      if (isControlled) {
        onEnabledDaysChange?.(next);
      } else {
        setEnabledDaysState(next);
      }
    }
  }, [normalized]);

  const setEnabledDays = (updater: EnabledDays | ((prev: EnabledDays) => EnabledDays)) => {
    const current = enabledDays || ({} as EnabledDays);
    const next = typeof updater === 'function' ? updater(current) : updater;
    if (isControlled) {
      onEnabledDaysChange?.(next);
      return;
    }
    setEnabledDaysState(next);
  };

  const updateDay = (day: DayEnum, updates: Partial<CourierSchedule>) => {
    const next = normalizeWorkingHours(value).map((item) => (item.day === day ? { ...item, ...updates } : item));
    onChange(next);
  };

  const handleToggleDay = (day: DayEnum) => (_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setEnabledDays((prev) => ({ ...prev, [day]: checked }));
    if (!checked) {
      updateDay(day, { startTime: '', endTime: '' });
    }
  };

  const handleTimeChange = (day: DayEnum, field: 'startTime' | 'endTime') => (newValue: TimeType) => {
    const formatted = newValue ? dayjs(newValue).format('HH:mm:ss') : '';
    updateDay(day, { [field]: formatted });
  };

  const getPickerValue = (value: string) => {
    return value ? dayjs(value, 'HH:mm:ss') : null;
  };

  return (
    <Grid container rowSpacing={2}>
      {normalized.map((entry) => {
        const isEnabled = enabledDays[entry.day];
        const isDisabled = disabled || !isEnabled;
        const hasStart = Boolean(entry.startTime);
        const hasEnd = Boolean(entry.endTime);
        const showError = isEnabled && (!hasStart || !hasEnd);

        return (
          <Grid item xs={12} key={entry.day}>
            <Grid container columnSpacing={0} rowSpacing={2} className={classes.dayRow}>
              <Grid item xs={12} md={3} className={classes.dayCell}>
                <FormControlLabel
                  className={classes.dayLabel}
                  control={
                    <Checkbox
                      color="secondary"
                      checked={isEnabled}
                      onChange={handleToggleDay(entry.day)}
                      inputProps={{ 'data-test-id': `SCHEDULE_DAY_${entry.day}` } as InputHTMLAttributes<HTMLInputElement>}
                    />
                  }
                  label={
                    <Typography variant="body2" weight="500" color="textPrimary">
                      {entry.day}
                    </Typography>
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Box className={classes.timeColumn}>
                  <Typography variant="body2" weight="500" color="textSecondary">
                    Start time
                  </Typography>
                  <TimePicker
                    label=""
                    value={getPickerValue(entry.startTime)}
                    disabled={isDisabled}
                    className={classes.timePickerCompact}
                    inputTestId={`SCHEDULE_START_${entry.day}`}
                    onChange={handleTimeChange(entry.day, 'startTime')}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box className={classes.timeColumn}>
                  <Typography variant="body2" weight="500" color="textSecondary">
                    End time
                  </Typography>
                  <TimePicker
                    label=""
                    value={getPickerValue(entry.endTime)}
                    disabled={isDisabled}
                    className={classes.timePickerCompact}
                    inputTestId={`SCHEDULE_END_${entry.day}`}
                    onChange={handleTimeChange(entry.day, 'endTime')}
                  />
                </Box>
              </Grid>
              {showError && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="error">
                    Start and end time are required for selected days.
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default WeeklySchedule;
